import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import BagViewer from "./BagViewer";
import StoryPanel from "./StoryPanel";
import CollectionGallery from "./CollectionGallery";
import ZoomModal from "./ZoomModal";
import SiteHeader from "./SiteHeader";
import ThailandMapPage from "./ThailandMap";
import { defaultCollection, findCollection, publishedCollections } from "./data/collections";
import { EXHIBITION_PATH, MAP_PATH, MAP_SLUG, reducedMotion, scrollToId } from "./routes";
import { bagParams, track } from "./analytics";
import { pick, provinceName, useLang } from "./i18n";
import "./bigc-exhibition.css";

export default function BigCExhibition() {
  const { slug } = useParams();
  return slug === MAP_SLUG ? <ThailandMapPage /> : <ExhibitionPage />;
}

// ผสมสีกับสีขาว (t = สัดส่วนสีขาว) — ทำสีธีมให้อ่อนพอที่ตัวหนังสือบนพื้นหลังยังอ่านง่าย
const tint = (hex, t) => {
  const n = parseInt(hex.slice(1), 16);
  const c = [n >> 16, (n >> 8) & 255, n & 255].map((v) => Math.round(v + (255 - v) * t));
  return `rgb(${c.join(",")})`;
};

const hotspotParams = (h) => ({ hotspot_id: h.id, hotspot_title: h.titleTh, hotspot_category: h.category || "", view: h.view });

function ExhibitionPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { lang, t, withLang } = useLang();
  const collection = findCollection(slug) || defaultCollection;

  const [activeView, setActiveView] = useState("front");
  const [selectedHotspotId, setSelectedHotspotId] = useState(null);
  const [viewRequest, setViewRequest] = useState(null);
  const [autoRotate, setAutoRotate] = useState(false);
  const [zoomTarget, setZoomTarget] = useState(null);

  // ไม่ระบุจังหวัด / deep link ที่ไม่รู้จัก → เปลี่ยน URL เป็นถุงเริ่มต้น (GA4 จะนับยอดดูตาม path ของถุงจริง)
  useEffect(() => {
    if (!slug || !findCollection(slug)) {
      navigate(`${EXHIBITION_PATH}/${defaultCollection.slug}${location.search}`, { replace: true, state: location.state });
    }
  }, [slug, navigate, location.state, location.search]);

  // เปลี่ยนจังหวัด → reset Front + zoom + hotspot และนับ bag_view (source มาจาก state ตอน navigate)
  useEffect(() => {
    setSelectedHotspotId(null);
    setZoomTarget(null);
    setViewRequest({ view: "front", resetZoom: true });
    track("bag_view", { ...bagParams(collection), source: location.state?.source || "direct" });
    // นับครั้งเดียวต่อการเปิดถุง ไม่ต้องรันซ้ำเมื่อ state เปลี่ยน
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collection.id]);

  const selectedHotspot = useMemo(
    () => collection.hotspots.find((h) => h.id === selectedHotspotId) || null,
    [collection, selectedHotspotId]
  );

  const handleViewChange = useCallback((view) => setActiveView(view), []);
  const closeZoom = useCallback(() => setZoomTarget(null), []);

  const openZoom = useCallback(
    (target, from) => {
      setZoomTarget(target);
      track("artwork_zoom", { ...bagParams(collection), view: target, from });
    },
    [collection]
  );
  const changeZoomTab = useCallback((target) => openZoom(target, "modal_tab"), [openZoom]);

  // from: bag (กด + บนถุง) | list (การ์ดจุดเด่น) | explore_button
  const handleHotspotSelect = useCallback(
    (id, { rotate = false, from } = {}) => {
      setSelectedHotspotId(id);
      const h = collection.hotspots.find((x) => x.id === id);
      if (rotate && h) setViewRequest({ view: h.view });
      if (h) track("hotspot_open", { ...bagParams(collection), ...hotspotParams(h), from: from || (rotate ? "list" : "bag") });
    },
    [collection]
  );

  // กดจุดในหน้าซูม: แสดง tooltip ในหน้าซูม และเลือกจุดนั้นในแผงเรื่องราวด้วย
  const handleZoomHotspot = useCallback(
    (h) => {
      setSelectedHotspotId(h.id);
      track("hotspot_open", { ...bagParams(collection), ...hotspotParams(h), from: "zoom" });
    },
    [collection]
  );

  const selectCollection = (c) => {
    if (c.id === collection.id) {
      scrollToId("bce-exhibit");
      return;
    }
    navigate(withLang(`${EXHIBITION_PATH}/${c.slug}`), { state: { source: "gallery" } });
    scrollToId("bce-exhibit");
  };

  const exploreHotspots = () => {
    const first = collection.hotspots.find((h) => h.view === activeView) || collection.hotspots[0];
    if (!first) return;
    // หมุนไปด้านที่มีจุดเด่นแล้วเปิด tooltip บนถุง
    setSelectedHotspotId(first.id);
    setViewRequest({ view: first.view, tipId: first.id });
    track("hotspot_open", { ...bagParams(collection), ...hotspotParams(first), from: "explore_button" });
  };

  const toggleAutoRotate = () => {
    if (!autoRotate) track("auto_rotate_start", bagParams(collection));
    setAutoRotate((v) => !v);
  };

  const [enTop, enBottom] = collection.provinceEn.split(/ (.+)/);
  const name = provinceName(collection, lang);

  return (
    <div className="bce" id="bce-top" lang={lang} style={{ "--bce-handle": collection.handleColor, "--bce-accent": collection.accent }}>
      <Helmet>
        <html lang={lang} />
        <title>{t("pageTitle", `${name}${collection.editionEn ? ` ${collection.editionEn}` : ""}`)}</title>
        <meta name="description" content={pick(collection, "teaser", lang)} />
      </Helmet>

      <SiteHeader
        links={[
          { label: t("navBags"), active: true, onClick: () => scrollToId("bce-exhibit") },
          { label: t("navMap"), onClick: () => navigate(withLang(MAP_PATH)) },
          { label: t("navGallery"), onClick: () => scrollToId("bce-gallery") },
          { label: t("navStory"), onClick: () => scrollToId("bce-story") },
        ]}
      />

      <main>
        <section className="bce-hero is-themed" id="bce-exhibit" aria-label={t("exhibitAria", name)}>
          {/* พื้นหลังล้อตามธีมถุง: ไล่สีจาก 3 สีเด่นของลาย + ภาพถุงเบลอจาง ๆ (key ใหม่ทุกถุง → ค่อย ๆ จางเข้า) */}
          <div
            key={`bg-${collection.id}`}
            className="bce-hero-theme"
            aria-hidden="true"
            style={{
              background: `linear-gradient(180deg, ${tint(collection.theme[0], 0.5)} 0%, ${tint(collection.theme[1], 0.7)} 55%, ${tint(
                collection.theme[2],
                0.85
              )} 85%, #f6f9fa 100%)`,
            }}
          />
          <div
            key={`blur-${collection.id}`}
            className="bce-hero-backdrop"
            aria-hidden="true"
            style={{ backgroundImage: `url("${collection.thumbnail}")` }}
          />
          <div className="bce-layout">
            <div className="bce-intro">
              <h1 lang="en">
                {enTop}
                {enBottom && (
                  <>
                    <br />
                    {enBottom}
                  </>
                )}
              </h1>
              <span className="bce-stroke" lang="en">
                {collection.editionEn || t("collection")}
              </span>
              <p className="bce-province-th" lang="th">
                {collection.provinceTh}
              </p>
              <p className="bce-teaser">{pick(collection, "teaser", lang)}</p>

              <div className="bce-actions">
                <button
                  type="button"
                  className="bce-action is-primary"
                  aria-pressed={autoRotate}
                  disabled={reducedMotion}
                  title={reducedMotion ? t("reducedMotion") : undefined}
                  onClick={toggleAutoRotate}
                >
                  <span aria-hidden="true">{autoRotate ? "❚❚" : "⟳"}</span>
                  {autoRotate ? t("stopRotate") : t("play360")}
                  <span aria-hidden="true">➜</span>
                </button>
                <button type="button" className="bce-action" onClick={() => openZoom(activeView, "zoom_button")}>
                  <span aria-hidden="true">⌕</span>
                  {t("zoomDetail")}
                  <span aria-hidden="true">＋</span>
                </button>
                <button type="button" className="bce-action" onClick={exploreHotspots} disabled={!collection.hotspots.length}>
                  <span aria-hidden="true">✦</span>
                  {t("exploreHotspots")}
                  <span aria-hidden="true">➜</span>
                </button>
                <button type="button" className="bce-action" onClick={() => scrollToId("bce-story")}>
                  <span aria-hidden="true">▤</span>
                  {t("storyInspiration")}
                  <span aria-hidden="true">➜</span>
                </button>
              </div>
            </div>

            <div className="bce-viewer-col">
              <BagViewer
                collection={collection}
                selectedHotspotId={selectedHotspotId}
                onHotspotSelect={handleHotspotSelect}
                onViewChange={handleViewChange}
                viewRequest={viewRequest}
                autoRotate={autoRotate}
                onAutoRotateChange={setAutoRotate}
              />
              <button type="button" className="bce-pill bce-artwork-btn" onClick={() => openZoom("artwork", "artwork_button")}>
                {t("viewArtwork")}
              </button>
            </div>

            <StoryPanel collection={collection} selectedHotspot={selectedHotspot} onHotspotSelect={handleHotspotSelect} />
          </div>
        </section>

        <CollectionGallery collections={publishedCollections} currentId={collection.id} onSelect={selectCollection} />

        <p className="bce-note" />
      </main>

      {zoomTarget && (
        <ZoomModal
          collection={collection}
          target={zoomTarget}
          onTargetChange={changeZoomTab}
          onClose={closeZoom}
          onHotspotOpen={handleZoomHotspot}
        />
      )}
    </div>
  );
}
